import { max, size, map, upperFirst } from 'lodash';
import { Component } from 'react';
import pt from 'prop-types';
import { Bar } from 'recharts';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, LocalizedEnumify } from '@mews/framework';
import { alpha } from '@optimus-web/core';
import TooltipContent from './TooltipContent';
import {
    COLOR_BAR_NORMAL,
    COLOR_BAR_OVERBOOKED,
    FULL_OPACITY,
    MAX_BAR_SIZE,
    MIN_BARS_TO_SHOW_LEGEND,
    OCCUPANCY_UNIT,
} from './constants';
import {
    DividerElement,
    getFillColor,
    LegendElement,
    TooltipItemElement,
    TooltipItemNameElement,
    TooltipMarkElement,
} from './style';
import { getBars, hasOverbookedValues } from './utils';
import ResourceCategoryTypeBar from './ResourceCategoryTypeBar';

const withOccupancyBars = (WrappedComponent: $TSFixMe, { tooltipProps = {} } = {}) =>
    class OccupancyBarsComponent extends Component {
        static propTypes = {
            data: pt.arrayOf(pt.shape({})).isRequired,
            maxYValue: pt.number.isRequired,
        };

        constructor(props: $TSFixMe) {
            super(props);
            this.state = {
                domain: this.getDomain(props),
                bars: getBars(props.data),
                hasOverbooked: hasOverbookedValues(props.data),
            };
        }

        UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
            if ((this.props as $TSFixMe).maxYValue !== nextProps.maxYValue) {
                this.setState({ domain: this.getDomain(nextProps) });
            }
            if ((this.props as $TSFixMe).data !== nextProps.data) {
                this.setState({
                    bars: getBars(nextProps.data),
                    hasOverbooked: hasOverbookedValues(nextProps.data),
                });
            }
        }

        getDomain = (props: $TSFixMe) => [0, max([props.maxYValue, 100])];

        isLegendVisible = () =>
            size((this.state as $TSFixMe).bars) >= MIN_BARS_TO_SHOW_LEGEND || (this.state as $TSFixMe).hasOverbooked;

        renderTooltip = (props: $TSFixMe) => {
            const mergedProps = {
                ...props,
                ...tooltipProps,
                payload: map(props.payload, (item) => ({
                    ...item,
                    name: upperFirst(item.name),
                    opacity: FULL_OPACITY,
                    color: item.color || getFillColor(item.value),
                })),
            };
            return <TooltipContent {...mergedProps} />;
        };

        renderLegendItem = (item: $TSFixMe) => (
            <TooltipItemElement key={`legend-item-${item.dataKey}`}>
                <TooltipMarkElement color={alpha(COLOR_BAR_NORMAL, item.fillOpacity)} />
                <TooltipItemNameElement>
                    <LocalizedEnumify enumValue={item.resourceCategoryType} />
                </TooltipItemNameElement>
            </TooltipItemElement>
        );

        renderOverbookedLegendItem = () => [
            <DividerElement key="divider" />,
            <TooltipItemElement key="legend-item-overbooked">
                <TooltipMarkElement color={COLOR_BAR_OVERBOOKED} />
                <TooltipItemNameElement>
                    <LocalizedText textKey={TextKey.Overbooking} />
                </TooltipItemNameElement>
            </TooltipItemElement>,
        ];

        renderLegend = () => {
            if (!this.isLegendVisible()) {
                return null;
            }

            return (
                <LegendElement>
                    {map((this.state as $TSFixMe).bars, this.renderLegendItem)}
                    {(this.state as $TSFixMe).hasOverbooked && this.renderOverbookedLegendItem()}
                </LegendElement>
            );
        };

        renderBars = ({ yAxisId = 0 } = {}) =>
            map((this.state as $TSFixMe).bars, (bar) => {
                const { resourceCategoryType, fillOpacity, dataKey } = bar;
                const shapeProps = {
                    fillOpacity,
                    unit: OCCUPANCY_UNIT,
                    resourceCategoryType,
                };
                const props = {
                    yAxisId,
                    dataKey,
                    key: resourceCategoryType.value,
                    name: resourceCategoryType.name,
                    unit: OCCUPANCY_UNIT,
                    shape: <ResourceCategoryTypeBar {...shapeProps} />,
                    maxBarSize: MAX_BAR_SIZE,
                    fillOpacity,
                };
                return <Bar {...props} />;
            });

        render() {
            const props = {
                occupancyDomain: (this.state as $TSFixMe).domain,
                renderTooltip: this.renderTooltip,
                renderBars: this.renderBars,
                renderLegend: this.renderLegend,
                ...this.props,
            };

            return <WrappedComponent {...props} />;
        }
    };

export default withOccupancyBars;
